import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Sparkles, CheckCircle2, AlertCircle, FileDown } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const COMMODITIES = ["Gold", "Copper", "PGM", "Coal", "Iron Ore", "Lithium", "Nickel", "Cobalt", "Hydrogen", "Water"];
const EQUIPMENT = [
    "Rip Tank", "Cyclone", "Thickener", "Pump", "Screen",
    "Flotation Cell", "Reactor", "Pressure Vessel", "Tank", "Piping Component",
];

export default function DataSheetIntelligence() {
    const [commodity, setCommodity] = useState("Gold");
    const [equipment, setEquipment] = useState("Rip Tank");
    const [sheet, setSheet] = useState(null);
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const generate = async () => {
        setBusy(true);
        setSheet(null);
        try {
            const { data } = await api.post("/edi/datasheet", { commodity, equipment });
            setSheet(data);
            toast.success("Data sheet generated");
        } catch {
            toast.error("Data sheet generation failed");
        } finally {
            setBusy(false);
        }
    };

    const exportPdf = async () => {
        if (!sheet) return;
        setDownloading(true);
        try {
            const token = localStorage.getItem("sgsb.token");
            const resp = await fetch(`${API_BASE}/reports/export`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    title: `Data Sheet — ${commodity} · ${equipment}`,
                    content: sheet.markdown,
                    fmt: "pdf",
                    confidentiality: "Executive",
                }),
            });
            if (!resp.ok) throw new Error(String(resp.status));
            const cd = resp.headers.get("Content-Disposition") || "";
            const match = cd.match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "SGSBateman_Data_Sheet.pdf";
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
            toast.success("Data sheet PDF downloaded");
        } catch {
            toast.error("Export failed");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid={EDI.datasheetRoot}>
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.22), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.14), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <FileSpreadsheet className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Engineering Data Sheet Intelligence™</span>
                    </div>
                    <h3 className="mt-1 font-display font-black text-white text-xl tracking-tight">
                        Generate a data sheet from historical designs
                    </h3>
                    <p className="text-slate-400 text-sm font-body mt-1 max-w-3xl">
                        Pick the commodity and equipment class — the platform searches the repository,
                        analyses similar historical designs, and auto-populates operating conditions,
                        materials, dimensions and vendor history. Engineers validate the differences,
                        not the entire document.
                    </p>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">Commodity</label>
                            <select
                                data-testid={EDI.datasheetCommodity}
                                value={commodity}
                                onChange={(e) => setCommodity(e.target.value)}
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            >
                                {COMMODITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">Equipment</label>
                            <select
                                data-testid={EDI.datasheetEquipment}
                                value={equipment}
                                onChange={(e) => setEquipment(e.target.value)}
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            >
                                {EQUIPMENT.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button
                                data-testid={EDI.datasheetGenerate}
                                disabled={busy}
                                onClick={generate}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                            >
                                <Sparkles className="h-4 w-4" strokeWidth={2} />
                                {busy ? "Generating…" : "Generate draft data sheet"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {sheet && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            ["Fields Auto-Populated", `${sheet.auto_populated_pct}%`],
                            ["Review Required",       `${sheet.review_required_pct}%`],
                            ["Historical Refs",       `${sheet.historical_refs}`],
                            ["Draft Time",            "1.8 h avg"],
                        ].map(([k, v]) => (
                            <div key={k} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                                <div className="kicker text-slate-400 text-[10px]">{k}</div>
                                <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        {sheet.sections.map((s) => (
                            <div key={s.name} className="glass rounded-xl overflow-hidden">
                                <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                                    <div className="font-display font-black text-white tracking-tight">{s.name}</div>
                                    <div className="flex-1" />
                                    <span className="text-[11px] rounded-full px-2 py-0.5 border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)]">
                                        {s.fields.length} fields
                                    </span>
                                </div>
                                <table className="w-full text-sm font-body">
                                    <tbody>
                                        {s.fields.map((f, i) => (
                                            <tr key={i} className={i === 0 ? "" : "border-t border-slate-800/60"}>
                                                <td className="px-4 py-2 w-8">
                                                    {f.auto
                                                        ? <CheckCircle2 className="h-4 w-4 text-emerald-400" strokeWidth={2} />
                                                        : <AlertCircle className="h-4 w-4 text-amber-400" strokeWidth={2} />}
                                                </td>
                                                <td className="px-2 py-2 text-slate-400 w-56">{f.label}</td>
                                                <td className="px-2 py-2 text-slate-100 font-medium">{f.value}</td>
                                                <td className="px-2 py-2 text-[11px] text-slate-500 text-right">{f.source}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>

                    <div className="glass rounded-xl p-5">
                        <div className="flex flex-wrap items-center gap-3">
                            <div>
                                <div className="kicker text-[var(--sgsb-gold)] text-[10px]">Ready to review</div>
                                <div className="font-display font-black text-white tracking-tight">
                                    Draft data sheet composed from {sheet.historical_refs} historical designs
                                </div>
                                <div className="text-xs text-slate-500 font-body mt-1">
                                    Green ticks are auto-populated fields. Amber flags require engineer validation.
                                </div>
                            </div>
                            <div className="flex-1" />
                            <button
                                data-testid={EDI.datasheetExport}
                                onClick={exportPdf}
                                disabled={downloading}
                                className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                            >
                                <FileDown className="h-4 w-4" strokeWidth={2} />
                                {downloading ? "Preparing…" : "Export as PDF"}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
